import { useState } from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import dayjs from "dayjs";
import LeagueComposeMsgDialog from "./LeagueComposeMsgDialog";
import request from "../../../utilities/requests";

const RECIPIENT_LABELS = {
  all: "All Attendees",
  byTeam: "By Team",
  custom: "Specific People",
};

// ── Scheduled Messages Dialog ──────────────────────────────────────
const ScheduledMsgDialog = ({ open, close, leagueEventId }) => {
  const [messages, setMessages] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const loadMessages = async () => {
    const result = await request(
      `templates/scheduledmessages/?leagueEventId=${leagueEventId}`
    );
    if (result.success) setMessages(result.data);
    setLoaded(true);
  };

  // Reload when dialog opens
  if (open && !loaded) loadMessages();
  if (!open && loaded) setLoaded(false);

  const handleDelete = async (id) => {
    const result = await request("templates/deletescheduledmessage", "post", { id });
    if (result.success) {
      setMessages((prev) => prev.filter((msg) => msg._id !== id));
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth>
      <DialogTitle>Scheduled Messages</DialogTitle>
      <DialogContent>
        {messages.length === 0 ? (
          <DialogContentText>No scheduled messages found.</DialogContentText>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Template</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Send At</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Recipients</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Channel</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {messages.map((msg) => (
                <TableRow key={msg._id}>
                  <TableCell>{msg.template}</TableCell>
                  <TableCell>
                    {dayjs(msg.sendAt).local().format("MM/DD/YY hh:mm A")}
                  </TableCell>
                  <TableCell>
                    {RECIPIENT_LABELS[msg.recipientFilter] ?? msg.recipientFilter}
                  </TableCell>
                  <TableCell>{msg.channel}</TableCell>
                  <TableCell>
                    {msg.status}
                    {msg.status === "failed" && msg.note && (
                      <div style={{ color: "gray", fontSize: "0.8em", marginTop: 4 }}>
                        {msg.note}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleDelete(msg._id)} size="small">
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Event Comm Log Dialog ──────────────────────────────────────────
const CommLogDialog = ({ open, close, eventId }) => {
  const [messages, setMessages] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const loadMessages = async () => {
    const result = await request(`communications/eventlog/?eventId=${eventId}`);
    if (result.success) setMessages(result.data);
    setLoaded(true);
  };

  if (open && !loaded) loadMessages();
  if (!open && loaded) setLoaded(false);

  return (
    <Dialog open={open} onClose={close} fullWidth>
      <DialogTitle>Event Communications</DialogTitle>
      <DialogContent>
        {messages.length === 0 ? (
          <DialogContentText>No event messages found.</DialogContentText>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Template/Subject</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Sent At</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Channel</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messages.map((msg) => (
                <TableRow key={msg._id}>
                  <TableCell>
                    {msg.template === "custom" ? msg.subject : msg.template}
                  </TableCell>
                  <TableCell>
                    {dayjs(msg.sentAt).local().format("MM/DD/YY hh:mm A")}
                  </TableCell>
                  <TableCell>{msg.channel}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main LeagueComms Tab ───────────────────────────────────────────
const LeagueComms = ({ eventId, leagueEvent }) => {
  const [composeOpen, setComposeOpen] = useState(false);
  const [scheduledOpen, setScheduledOpen] = useState(false);
  const [commLogOpen, setCommLogOpen] = useState(false);

  return (
    <Box sx={{ p: 3, maxWidth: 400 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Messaging
      </Typography>
      <Stack spacing={2}>
        <Button variant="outlined" size="small" onClick={() => setComposeOpen(true)}>
          Compose Message
        </Button>
        <Button variant="outlined" size="small" onClick={() => setScheduledOpen(true)}>
          Scheduled Messages
        </Button>
        <Button variant="outlined" size="small" onClick={() => setCommLogOpen(true)}>
          Event Comm Log
        </Button>
      </Stack>

      <LeagueComposeMsgDialog
        open={composeOpen}
        close={() => setComposeOpen(false)}
        leagueEvent={leagueEvent}
      />
      <ScheduledMsgDialog
        open={scheduledOpen}
        close={() => setScheduledOpen(false)}
        leagueEventId={leagueEvent?._id}
      />
      <CommLogDialog
        open={commLogOpen}
        close={() => setCommLogOpen(false)}
        eventId={eventId}
      />
    </Box>
  );
};

export default LeagueComms;
